import { createApi } from "@reduxjs/toolkit/query/react"
import {
  setDKIM,
  setDomainId,
  setEmailProvider,
  setName,
  setProvider,
  setUrlRedirect,
} from "../domainSetupSlice"
import { baseQueryWithReauth } from "@/core/api/baseQuery"
import { buildQueryString } from "@/utils/strings"
import type {
  Domain,
  DomainDNS,
  DomainVerifyResponse,
  InboxingEntitlements,
} from "@/types/entities/domain"
import type { EmailAddress } from "@/types/entities/email-address"

export type InboxingDomainsResponse = {
  items: Domain[]
  total: number
  entitlements?: InboxingEntitlements
}

export type InboxingDomainsQuery = {
  orgId: string
  campaignId?: string
  folderOnly?: boolean
  text?: string
  status?: string
  tag?: string
}

export type InboxingDomainMutationResponse = {
  domain: Domain
  entitlements?: InboxingEntitlements
}

export type InboxingMailboxMutationResponse = {
  domain?: Domain
  mailboxes?: EmailAddress[]
  entitlements?: InboxingEntitlements
}

export type InboxingDomainPolicyPayload = {
  orgId: string
  domainId: string
  domainDailyCap?: number
  tenantDailyCap?: number
  catchAllEmail?: string
}

type InboxingMailboxMutationPayload = {
  orgId: string
  domainId: string
  mailboxIds: string[]
}

export const domainsApi = createApi({
  reducerPath: "domainsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Domains", "DNS Records", "Email Domains"],
  endpoints: (builder) => ({
    getDomains: builder.query<
      { items: Domain[]; total: number },
      { orgId: string; skip?: number; limit?: number; text?: string }
    >({
      query: ({ orgId, ...keys }) => ({
        method: "GET",
        url: `org/${orgId}/domains?${buildQueryString(keys)}`,
      }),
      transformResponse: ({
        data: { items, total },
      }: {
        data: { items: Domain[]; total: number }
      }) => ({
        items,
        total,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ _id }) => ({
                type: "Domains" as const,
                _id,
              })),
              { type: "Domains", id: "PARTIAL-LIST" },
            ]
          : [{ type: "Domains", id: "PARTIAL-LIST" }],
    }),
    getDomain: builder.query<Domain, { id: string }>({
      query: ({ id }) => ({
        method: "GET",
        url: `domain/${id}`,
      }),
      transformResponse: ({ data }: { data: Domain }) => data,
      providesTags: (result, error, { id }) => [{ type: "Domains", id }],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(setDomainId(data._id))
        dispatch(setName(data.name))
        dispatch(setProvider(data.provider))
        if (data.urlRedirect) {
          dispatch(setUrlRedirect(data.urlRedirect))
        }
        if (data.emailProvider) {
          dispatch(setEmailProvider(data.emailProvider))
        }
        if (data.extra?.DKIM) {
          dispatch(setDKIM(data.extra.DKIM.address))
        }
      },
    }),
    getDNSRecords: builder.query<DomainDNS, { id: string }>({
      query: ({ id }) => ({
        method: "GET",
        url: `domain/${id}/dnsRecords`,
      }),
      transformResponse: ({ data }: { data: DomainDNS }) => data,
      providesTags: ["DNS Records"],
    }),
    addDomain: builder.mutation<
      Domain,
      { orgId: string; name: string; provider: string; urlRedirect: string }
    >({
      query: (body) => ({
        method: "POST",
        url: "domain",
        body,
      }),
      invalidatesTags: ["Domains"],
      transformResponse: ({ data }: { data: Domain }) => data,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          if (data) {
            dispatch(setDomainId(data._id))
          }
        } catch (e) {
          console.error(e)
        }
      },
    }),
    setupDomain: builder.mutation<
      Domain,
      { id: string; body: { emailProvider: string } }
    >({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `domain/${id}/setup`,
        body,
      }),
      invalidatesTags: ["Domains"],
      transformResponse: ({ data }: { data: Domain }) => data,
    }),
    addExtraDomain: builder.mutation<
      Domain,
      { body: { DKIM: string }; id: string }
    >({
      query: ({ body, id }) => ({
        method: "PUT",
        url: `domain/${id}/extra`,
        body,
      }),
      transformResponse: ({ data }: { data: Domain }) => data,
    }),
    verifyDomain: builder.mutation<
      DomainVerifyResponse,
      { id: string; type: "ownership" | "dnsRecords" }
    >({
      query: ({ id, type }) => ({
        method: "PUT",
        url: `domain/${id}/verify?${buildQueryString({ type })}`,
      }),
      invalidatesTags: ["Domains"],
      transformResponse: ({ data }: { data: DomainVerifyResponse }) => data,
    }),
    deleteDomain: builder.mutation<{ data: boolean }, { id: string }>({
      query: ({ id }) => ({
        method: "DELETE",
        url: `domain/${id}`,
      }),
      invalidatesTags: ["Domains"],
    }),
    getEmailDomains: builder.query<
      { items: Domain[]; total: number },
      { id: string }
    >({
      query: ({ id }) => ({
        method: "GET",
        url: `org/${id}/domains/available`,
      }),
      transformResponse: ({
        data: { items, total },
      }: {
        data: { items: Domain[]; total: number }
      }) => ({
        items,
        total,
      }),
      providesTags: ["Email Domains"],
    }),
    getInboxingDomains: builder.query<
      InboxingDomainsResponse,
      InboxingDomainsQuery
    >({
      query: ({ orgId, ...keys }) => ({
        method: "GET",
        url: `org/${orgId}/inboxingDomains?${buildQueryString(keys)}`,
      }),
      transformResponse: ({
        data: { items, total, entitlements },
      }: {
        data: InboxingDomainsResponse
      }) => ({
        items,
        total,
        entitlements,
      }),
      providesTags: ["Domains"],
    }),
    enableInboxingDomain: builder.mutation<
      InboxingDomainMutationResponse,
      {
        orgId: string
        domainId: string
        defaultSenderId?: string
        entitlements?: InboxingEntitlements
        importCsv?: boolean
        importMailboxes?: boolean
      }
    >({
      query: ({
        orgId,
        domainId,
        defaultSenderId,
        entitlements,
        importCsv,
        importMailboxes,
      }) => {
        const body = {
          importCsv: importCsv ?? true,
          importMailboxes: importMailboxes ?? true,
          ...(defaultSenderId ? { defaultSenderId } : {}),
          ...(entitlements ? { entitlements } : {}),
        }

        return {
          method: "POST",
          url: `org/${orgId}/inboxing/domains/${domainId}/enable`,
          body,
        }
      },
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({ data }: { data: InboxingDomainMutationResponse }) =>
        data,
    }),
    disableInboxingDomain: builder.mutation<
      InboxingDomainMutationResponse,
      { orgId: string; domainId: string }
    >({
      query: ({ orgId, domainId }) => ({
        method: "POST",
        url: `org/${orgId}/inboxing/domains/${domainId}/disable`,
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({ data }: { data: InboxingDomainMutationResponse }) =>
        data,
    }),
    addDomainsToFolder: builder.mutation<
      { action: string; matched: number; modified: number },
      { orgId: string; domainIds: string[] }
    >({
      query: ({ orgId, domainIds }) => ({
        method: "POST",
        url: `org/${orgId}/inboxing/domains/folder/add`,
        body: { domainIds },
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({
        data,
      }: {
        data: { action: string; matched: number; modified: number }
      }) => data,
    }),
    syncInboxingDomains: builder.mutation<
      InboxingDomainsResponse,
      { orgId: string; domains?: Domain[]; targetInboxingOrgId?: string }
    >({
      query: ({ orgId, domains, targetInboxingOrgId }) => ({
        method: "POST",
        url: `org/${orgId}/inboxing/sync`,
        body:
          domains || targetInboxingOrgId
            ? {
                ...(domains ? { data: domains } : {}),
                ...(targetInboxingOrgId ? { targetInboxingOrgId } : {}),
              }
            : undefined,
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({
        data: { items, total, entitlements },
      }: {
        data: InboxingDomainsResponse
      }) => ({ items, total, entitlements }),
    }),
    bulkReassignInboxingMailboxes: builder.mutation<
      InboxingMailboxMutationResponse,
      InboxingMailboxMutationPayload & { defaultSenderId: string }
    >({
      query: ({ orgId, domainId, mailboxIds, defaultSenderId }) => ({
        method: "POST",
        url: `org/${orgId}/inboxing/domains/${domainId}/mailboxes/default-sender`,
        body: {
          mailboxIds,
          defaultSenderId,
        },
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({
        data,
      }: {
        data: InboxingMailboxMutationResponse
      }) => data,
    }),
    pauseInboxingMailboxes: builder.mutation<
      InboxingMailboxMutationResponse,
      InboxingMailboxMutationPayload
    >({
      query: ({ orgId, domainId, mailboxIds }) => ({
        method: "POST",
        url: `org/${orgId}/inboxing/domains/${domainId}/mailboxes/pause`,
        body: { mailboxIds },
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({
        data,
      }: {
        data: InboxingMailboxMutationResponse
      }) => data,
    }),
    resumeInboxingMailboxes: builder.mutation<
      InboxingMailboxMutationResponse,
      InboxingMailboxMutationPayload
    >({
      query: ({ orgId, domainId, mailboxIds }) => ({
        method: "POST",
        url: `org/${orgId}/inboxing/domains/${domainId}/mailboxes/resume`,
        body: { mailboxIds },
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({
        data,
      }: {
        data: InboxingMailboxMutationResponse
      }) => data,
    }),
    updateInboxingDomainPolicy: builder.mutation<
      InboxingDomainMutationResponse,
      InboxingDomainPolicyPayload
    >({
      query: ({ orgId, domainId, ...body }) => ({
        method: "PUT",
        url: `org/${orgId}/inboxing/domains/${domainId}/policy`,
        body,
      }),
      invalidatesTags: ["Domains", "Email Domains"],
      transformResponse: ({ data }: { data: InboxingDomainMutationResponse }) =>
        data,
    }),
  }),
})

export const {
  useGetDomainsQuery,
  useAddDomainMutation,
  useGetDomainQuery,
  useGetDNSRecordsQuery,
  useSetupDomainMutation,
  useAddExtraDomainMutation,
  useVerifyDomainMutation,
  useDeleteDomainMutation,
  useGetEmailDomainsQuery,
  useGetInboxingDomainsQuery,
  useEnableInboxingDomainMutation,
  useDisableInboxingDomainMutation,
  useAddDomainsToFolderMutation,
  useSyncInboxingDomainsMutation,
  useBulkReassignInboxingMailboxesMutation,
  usePauseInboxingMailboxesMutation,
  useResumeInboxingMailboxesMutation,
  useUpdateInboxingDomainPolicyMutation,
} = domainsApi
